import { Link, useFetcher } from "@remix-run/react";
import { IconBrandInstagram } from "@tabler/icons-react";
import { useEffect, useState } from "react";

// Define the expected response type
type SubscribeResponse = {
  success?: boolean;
  message?: string;
};

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const fetcher = useFetcher<SubscribeResponse>();
  
  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data) {
      if (fetcher.data.success) {
        setStatus("success");
      } else {
        setStatus("error");
      }
      
      // Reset status after 3 seconds
      const timer = setTimeout(() => {
        setStatus("idle");
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [fetcher.state, fetcher.data]);

  return (
    <footer className="p-6 bg-[#2b2b2b] md:p-10">
      <div className="mx-auto max-w-screen-xl">
        {/* Top row: brand/newsletter + link columns */}
        <div className="md:flex md:justify-between md:gap-8">
          {/* Left side: Brand + Newsletter */}
          <div className="mb-6 md:mb-0 md:w-1/2 text-center md:text-left text-white lg:w-1/3">
            <img
              src="/images/logo-icon.svg"
              className="mx-auto md:ml-0 md:mr-3 h-20 w-20 mb-4"
              alt="VenerationWare"
            />

            {/* Newsletter Section */}
            <h2 className="text-lg font-semibold mb-1 uppercase">Stay in the loop</h2>
            <p className="text-sm mb-4">
              Sign up with your email address to receive news and updates.
            </p>
            <fetcher.Form className="flex flex-col sm:flex-row" action="/api/kitSignup" method="POST">
              {status != "success" && <><input
                type="email"
                name="email"
                placeholder="Email Address"
                required
                className="mb-2 sm:mb-0 sm:mr-2 px-5 py-3 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-gray-500 w-full sm:w-auto flex-1"
              />
              <button
                type="submit"
                className="bg-[#d9d9d9] text-black px-5 py-3 rounded-lg hover:brightness-90 transition-colors"
              >
                Sign Up
              </button>
              </>}
            </fetcher.Form>
            {status === "success" && (
              <p className="text-green-400 mt-2 text-sm">Successfully subscribed!</p>
            )}
            {status === "error" && (
              <p className="text-red-400 mt-2 text-sm">Failed to subscribe. Please try again.</p>
            )}
          </div>

          {/* Right side: ONLY Information links */}
          <div className="md:w-1/2 text-white lg:w-2/3 flex flex-col justify-center">
            <ul className="text-center md:text-right uppercase">
              <li className="mb-4">
                <a href="/contact-us" className="hover:underline">
                  Contact Us
                </a>
              </li>
              <li className="mb-4">
                <a href="/faqs" className="hover:underline">
                  FAQs
                </a>
              </li>
              <li className="mb-4">
                <a href="/privacy-policy" className="hover:underline">
                  Privacy Policy
                </a>
              </li>
              <li className="mb-4">
                <a href="/shipping-and-returns" className="hover:underline">
                  Shipping &amp; Returns
                </a>
              </li>
              <li>
                <a href="/terms-of-service" className="hover:underline">
                  Terms of Service
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <hr className="my-6 border-gray-200 sm:mx-auto dark:border-gray-700 lg:my-8" />

        {/* Bottom row: copyright & social icons */}
        <div className="flex items-center justify-between text-white">
          <span className="text-sm sm:text-center">
            © 2025{" "}
            <Link to="/" className="hover:underline">
              VenerationWear
            </Link>
            . All Rights Reserved.
          </span>
          <div className="flex space-x-6 sm:justify-center sm:mt-0">
            {/* Instagram icon (added from original) */}
            <a
              href="http://instagram.com/venerationwear"
              target="_blank"
              rel="noreferrer"
              className="hover:text-gray-300"
              aria-label="Instagram"
            >
              <IconBrandInstagram className="w-7 h-7" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
